import React, { useEffect, useRef, useState } from "react";
import Reveal from "../anim/Reveal";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./CredibilityStrip.module.css";

/* CredibilityStrip — "BUILT & RUN ON": the technology names, as typography.

   The layout is the reference's PARTNERS & CAMPAIGNS block: a two-line lockup
   on the left (bold sans over serif) with a lime hand-drawn stroke behind it, a
   short lead beside it, and ONE monochrome row underneath. Where the reference
   runs partner logos, this runs NAMES.

   HONESTY NOTE — deliberate omissions, please read before editing:
   • NO LOGOS, of any kind. The old TrustedByLogos drew each client as an
     INVENTED geometric mark (wave / leaf / ring / peak ...) — not the clients'
     real logos, so they are gone. And the technology vendors' marks are
     trademarks Appinfigo holds no rights to reproduce. So the row is names
     only, set in the site's own display face. If real, licensed logo files ever
     land in public/images, swap a name for an <img> here (PUBLIC_URL in JS —
     see CLAUDE.md) and keep the alt text equal to the name.
   • The technology names ARE real (SITE_CONTENT.techStack.items) — third-party
     tools the company genuinely builds on. Nothing is added to that list here.
   • The old Awards section listed ten recognitions ("B2B Tech Review 2026",
     "Cloud Excellence Awards 2025", ...) that were placeholder inventions, plus
     a decorative gold laurel/medallion. Appinfigo holds none of them, so the
     whole block stays dropped. Do NOT reintroduce an awards row unless a real,
     nameable, verifiable award exists.
   • The "shipped and live" product-name list that used to sit here is gone on
     purpose: the work grid (CaseStudies) and the Collage carry the products
     now, with their real screenshots. Do not duplicate them here.
   • The lime stroke is a drawn SHAPE (the global .scribble mask), not text and
     not a handwriting font. Lime is never a text colour on paper.
   • PAPER ONLY. The stroke runs behind INK text (ink on lime 13.94:1). On an
     olive / void tone the headline turns bone, and bone on lime is unreadable —
     so do not change this Section's tone without removing the stroke. */

// TODO(content): the two-line lockup. `sans` sets in the bold condensed sans,
// `serif` in the serif accent on the line below it. Keep both short.
const LOCKUP = {
  sans: "Built",
  serif: "& run on"
};

const TITLE_ID = "built-with-title";
const LIST_LABEL_ID = "built-with-stack";

const STACK = SITE_CONTENT.techStack || {};

/* Both strings come straight from SITE_CONTENT.techStack; an empty one simply
   does not render — nothing is written around a gap. */
const LEAD = typeof STACK.subtitle === "string" ? STACK.subtitle.trim() : "";
const LIST_LABEL = typeof STACK.title === "string" ? STACK.title.trim() : "";

/* Real technology names. Flat list today; also flattens a grouped shape so the
   row keeps working if techStack is ever restructured into categories. An entry
   that is not a non-empty string is skipped rather than invented around. */
const TECH = (Array.isArray(STACK.items) ? STACK.items : []).flatMap((entry) => {
  if (typeof entry === "string") return entry.trim() ? [entry.trim()] : [];
  if (entry && Array.isArray(entry.items)) {
    return entry.items.filter((n) => typeof n === "string" && n.trim()).map((n) => n.trim());
  }
  return [];
});

/* 45ms a name: the row reads left to right like a line being set, and the last
   of twelve names still starts inside half a second. Capped so a longer list
   never crawls in. */
function staggerDelay(index) {
  return Math.min(index, 11) * 45;
}

/* True once the observed element has been on screen. Drives the one-off
   "drawn" state of the lime stroke. No rAF, no scroll listener: a single
   IntersectionObserver that disconnects itself after firing. Where the API is
   missing the stroke is simply shown. The motion itself lives in CSS and is
   switched off there under prefers-reduced-motion. */
function useSeenOnce() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, seen];
}

export default function CredibilityStrip() {
  /* The LOCKUP is observed, not the stroke: the stroke starts fully clipped. */
  const [lockupRef, drawn] = useSeenOnce();

  const flourishClass = ["scribble", styles.flourish, drawn ? styles.flourishDrawn : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <Section tone="paper" size="std" id="credibility" aria-labelledby={TITLE_ID}>
      <div className={styles.top}>
        <div className={styles.lockup} ref={lockupRef}>
          {/* Decorative lime stroke BEHIND the lockup (the reference's big lime
              handwriting). It is the global .scribble primitive — a masked
              drawn path — resized by this module; never text, never an <svg>
              of our own. */}
          <span className={flourishClass} aria-hidden="true" />
          {/* An h2 inside <main>: HeadingWipe (App.js) clip-wipes it. Never wrap
              it in Reveal and never animate it here. */}
          <Statement as="h2" size="h2" id={TITLE_ID}>
            {LOCKUP.sans}
            <br />
            <em>{LOCKUP.serif}</em>
          </Statement>
        </div>

        {LEAD ? (
          <Reveal as="p" className={styles.lead} delay={120}>
            {LEAD}
          </Reveal>
        ) : null}
      </div>

      {TECH.length ? (
        <div className={styles.rail}>
          {LIST_LABEL ? (
            <MicroLabel as="p" id={LIST_LABEL_ID} className={styles.railLabel}>
              {LIST_LABEL}
            </MicroLabel>
          ) : null}
          {/* role="list" is redundant per the HTML spec, but it is load-bearing
              here: .names sets list-style:none, and Safari removes list
              semantics from a list whose marker is switched off — VoiceOver then
              stops announcing "list, N items" and drops the item positions,
              which is the only thing telling a screen-reader user how many
              technologies there are. Keeping the role costs nothing and restores
              that, so the lint rule is silenced for this one element rather than
              the attribute removed. */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul
            className={styles.names}
            role="list"
            aria-labelledby={LIST_LABEL ? LIST_LABEL_ID : TITLE_ID}
          >
            {TECH.map((name, index) => (
              /* The <li> owns the hairlines and stays put; only the name rises,
                 so the rules read as a fixed ledger the names are set into. */
              <li className={styles.name} key={`${name}-${index}`}>
                <Reveal
                  as="span"
                  className={styles.nameText}
                  direction="up"
                  delay={staggerDelay(index)}
                >
                  {name}
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  );
}
